/** Generic org onboarding wizard for the new 8-layer sprint additions:
 *   · Advertising Agency (L2)  → /onboard/advertising-agency
 *   · Studio · Network · Streamer (L5) → /onboard/studio-network
 *   · Event Production (L6)   → /onboard/event-production
 *
 *  These share the same shape (KYC + owner account) so a single
 *  configurable component keeps things DRY. L5 adds one extra field:
 *  `studio_network_kind` (broadcaster / streamer / major_studio).
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Stepper, Card, Field, NavRow } from "@/components/OnboardKit";
import OnboardBackButton from "@/components/OnboardBackButton";
import CertificationsPicker from "@/components/CertificationsPicker";
import AdvertisingAffiliationsPicker from "@/components/AdvertisingAffiliationsPicker";
import AffiliationsPicker from "@/components/AffiliationsPicker";
import { toast } from "sonner";
import { Megaphone, Tv, Sparkles, ShieldCheck, Newspaper, Users, Plus, Trash2, Check, Send, Film, Globe2, Drama, Theater } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const STEPS = ["Organization", "Owner Account", "Approved"];

// Static config per kind — keeps the component pure & easy to reason about.
const KIND_CFG = {
  advertising_agency: {
    layer: "L2",
    label: "Advertising Agency",
    testidPrefix: "onboard-adv-agency",
    accent: "#F59E0B",
    Icon: Megaphone,
    orgFieldLabel: "Agency Legal Name",
    brandFieldLabel: "Brand / Display Name",
    tagline: "Bridge from brand IP to executing production houses.",
  },
  studio_network: {
    layer: "L5",
    label: "Studio · Network · Streamer",
    testidPrefix: "onboard-studio-network",
    accent: "#0EA5E9",
    Icon: Tv,
    orgFieldLabel: "Entity Legal Name",
    brandFieldLabel: "Public / Consumer Brand",
    tagline: "Macro slates, IP management, distribution timelines.",
    hasStudioNetworkKind: true,
  },
  event_production: {
    layer: "L6",
    label: "Event Production Company",
    testidPrefix: "onboard-event-production",
    accent: "#EC4899",
    Icon: Sparkles,
    orgFieldLabel: "Company Legal Name",
    brandFieldLabel: "Public Brand",
    tagline: "Live experiences, experiential marketing, physical activations.",
    subtypeEndpoint:   "/meta/event-types",
    subtypePayloadKey: "event_types",
    subtypeLabel:      "Event Practice Areas",
    subtypeHint:       "Tick every event format you produce. Multiple practices are common.",
  },
  pr_agency: {
    layer: "L9",
    label: "PR Agency",
    testidPrefix: "onboard-pr-agency",
    accent: "#22D3EE",
    Icon: Newspaper,
    orgFieldLabel: "Agency Legal Name",
    brandFieldLabel: "Public / Brand Name",
    tagline: "Media relations, press management, sponsor for journalist verifications.",
    subtypeEndpoint:   "/meta/pr-practices",
    subtypePayloadKey: "pr_practices",
    subtypeLabel:      "PR Practice Areas",
    subtypeHint:       "Tick every PR practice you run. Multi-practice agencies are common.",
  },
  casting_agency: {
    layer: "L11",
    label: "Casting Agency",
    testidPrefix: "onboard-casting-agency",
    accent: "#A78BFA",
    Icon: Users,
    orgFieldLabel: "Agency Legal Name",
    brandFieldLabel: "Public / Brand Name",
    tagline: "Represent talent & models, sponsor talent verifications on the platform.",
  },
  talent_management_agency: {
    layer: "L11",
    label: "Talent Management Agency",
    testidPrefix: "onboard-talent-mgmt-agency",
    accent: "#A78BFA",
    Icon: Users,
    orgFieldLabel: "Agency Legal Name",
    brandFieldLabel: "Public / Brand Name",
    tagline: "Represent your exclusive talent roster — pitch to producers, brands & events.",
  },
  distributor: {
    layer: "L13",
    label: "Distributor",
    testidPrefix: "onboard-distributor",
    accent: "#0F766E",
    Icon: Send,
    orgFieldLabel: "Distribution House Legal Name",
    brandFieldLabel: "Trade / Brand Name",
    tagline: "Move films from producer to audience — theatrical, OTT, TV, overseas.",
    subtypeEndpoint:   "/meta/distribution-kinds",
    subtypePayloadKey: "distribution_kinds",
    subtypeLabel:      "Distribution Windows",
    subtypeHint:       "Tick every window you operate. Most distributors do 2-3 (e.g. theatrical + OTT, or theatrical + music).",
  },
  sales_agent: {
    layer: "L13",
    label: "Sales Agent",
    testidPrefix: "onboard-sales-agent",
    accent: "#DB6D19",
    Icon: Globe2,
    orgFieldLabel: "Sales Agency Legal Name",
    brandFieldLabel: "Trade / Brand Name",
    tagline: "Represent films' rights and sell territory by territory to global distributors.",
    subtypeEndpoint:   "/meta/sales-agent-markets",
    subtypePayloadKey: "sales_agent_markets",
    subtypeLabel:      "Markets You Attend",
    subtypeHint:       "Tick every international market where you actively pitch films. Most sales agents work 3-5 markets a year.",
  },
  exhibitor: {
    layer: "L14",
    label: "Exhibitor",
    testidPrefix: "onboard-exhibitor",
    accent: "#DC2626",
    Icon: Film,
    orgFieldLabel: "Exhibitor / Chain Legal Name",
    brandFieldLabel: "Cinema Brand (e.g. PVR, Miraj, Sathyam)",
    tagline: "Multiplex chains, mini-multiplexes, single-screens, drive-ins.",
  },
  theater_group: {
    layer: "L17",
    label: "Theater Group",
    testidPrefix: "onboard-theater-group",
    accent: "#F97316",
    Icon: Drama,
    orgFieldLabel: "Group Legal Name",
    brandFieldLabel: "Public / Brand Name",
    tagline: "Repertory ensembles, folk troupes, comedy collectives — originate plays and stage shows.",
    subtypeEndpoint:   "/meta/theater-styles",
    subtypePayloadKey: "theater_styles",
    subtypeLabel:      "Theatrical Styles",
    subtypeHint:       "Tick every style your group works in. Groups typically span 1-3 styles.",
  },
  theater_hall: {
    layer: "L18",
    label: "Theater Hall / Venue",
    testidPrefix: "onboard-theater-hall",
    accent: "#7C3AED",
    Icon: Theater,
    orgFieldLabel: "Venue Legal Name",
    brandFieldLabel: "Public / Brand Name",
    tagline: "Proscenium, thrust, black-box or amphitheatre — list your hall so groups and event producers can book you online.",
    hasTheaterHallFields: true,
  },
};

const STUDIO_NETWORK_KINDS = [
  { key: "broadcaster",  label: "Broadcaster (TV network umbrella)" },
  { key: "tv_channel",   label: "TV Channel (individual channel — Star Plus, Colors, etc.)" },
  { key: "streamer",     label: "Streaming Platform (Netflix / Prime / Hotstar)" },
  { key: "major_studio", label: "Major Studio (Warner Bros / Universal / Disney)" },
];

export default function OnboardGeneric({ kind }) {
  const cfg = KIND_CFG[kind];
  if (!cfg) throw new Error(`OnboardGeneric: unknown kind '${kind}'`);
  const { Icon } = cfg;

  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Agency-type selection passed in from `/onboard/agency-type` picker.
  // Only meaningful for kind === "advertising_agency"; ignored elsewhere.
  const location = useLocation();
  const [agencyTypes] = useState(
    () => (location.state?.agencyTypes || []).filter(Boolean),
  );
  const [agencyTypeCatalog, setAgencyTypeCatalog] = useState(null);
  useEffect(() => {
    if (kind !== "advertising_agency") return;
    axios.get(`${API}/meta/agency-types`)
         .then(({ data }) => setAgencyTypeCatalog(data))
         .catch(() => setAgencyTypeCatalog(null));
  }, []);
  // If someone lands on this URL directly without picking types first,
  // bounce them back to the picker (agency signups only).
  useEffect(() => {
    if (kind === "advertising_agency" && agencyTypes.length === 0) {
      nav("/onboard/agency-type", { replace: true });
    }
  }, []);   // eslint-disable-line

  // 0-2 optional co-owners for advertising agencies. Each row: {name, email, phone}
  const [coOwners, setCoOwners] = useState([]);
  const addCoOwner = () => {
    if (coOwners.length >= 2) return;
    setCoOwners((s) => [...s, { name: "", email: "", phone: "" }]);
  };
  const rmCoOwner = (idx) => setCoOwners((s) => s.filter((_, i) => i !== idx));
  const updCoOwner = (idx, k, v) =>
    setCoOwners((s) => s.map((c, i) => (i === idx ? { ...c, [k]: v } : c)));

  // Office geofence radius (advertising_agency only). Admin-configurable
  // 50-1000m; default 150m. Placed on Step 0 alongside company details.
  const [officeGeoRadius, setOfficeGeoRadius] = useState(150);

  const [org, setOrg] = useState({
    legal_name: "", brand_name: "", registered_address: "",
    gstin: "", pan: "",
    studio_network_kind: "streamer", // ignored unless kind === studio_network
    // theater_hall fields (ignored unless kind === theater_hall)
    theater_hall_stage_type: "proscenium",
    theater_hall_seats: 200,
    theater_hall_stage_width: 12,
    theater_hall_stage_depth: 8,
    theater_hall_stage_height: 6,
    theater_hall_tech_specs: "",
    theater_hall_evening_rate: 30000,
    theater_hall_matinee_rate: 20000,
    theater_hall_registered_city: "",
  });
  const [hallStageTypeCatalog, setHallStageTypeCatalog] = useState([]);
  useEffect(() => {
    if (!cfg.hasTheaterHallFields) return;
    axios.get(`${API}/meta/theater-hall-stage-types`)
      .then(({ data }) => setHallStageTypeCatalog(Array.isArray(data) ? data : []))
      .catch(() => setHallStageTypeCatalog([]));
  }, [kind]);
  const [owner, setOwner] = useState({
    owner_name: "", owner_email: "", owner_phone: "", owner_password: "",
  });
  const [approved, setApproved] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [advAffiliations, setAdvAffiliations] = useState([]);
  const [sectionAffiliations, setSectionAffiliations] = useState([]);
  // Sub-type multi-select (event_production · pr_agency). Only used when
  // KIND_CFG[kind].subtypeEndpoint is present.
  const [subtypeCatalog, setSubtypeCatalog] = useState([]);
  const [subtypes, setSubtypes] = useState(new Set());
  useEffect(() => {
    if (!cfg.subtypeEndpoint) return;
    axios.get(`${API}${cfg.subtypeEndpoint}`)
      .then(({ data }) => setSubtypeCatalog(Array.isArray(data) ? data : []))
      .catch(() => setSubtypeCatalog([]));
  }, [kind]);
  const toggleSubtype = (key) => setSubtypes((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  // Sections (other than advertising_agency) whose L2/L5/L6/L9/L10
  // onboarding flow drives the new generic AffiliationsPicker.
  const OTHER_AFF_SECTIONS = ["studio_network", "event_production",
                              "pr_agency", "casting_agency",
                              "talent_management_agency"];
  const usesGenericPicker  = OTHER_AFF_SECTIONS.includes(kind);

  const upd = (setter) => (k, v) => setter((p) => ({ ...p, [k]: v }));
  const updOrg   = upd(setOrg);
  const updOwner = upd(setOwner);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canNextStep0 = (() => {
    if (org.legal_name.trim().length < 2) return false;
    // Advertising Agencies must tick at least one affiliation before Step 1.
    if (kind === "advertising_agency" && advAffiliations.length === 0) return false;
    // Sections driven by generic picker: enforce their min_required client-side.
    // (Server also enforces — this is only for the Continue-button state.)
    if (usesGenericPicker && sectionAffiliations.length === 0) return false;
    // Sub-type multi-select (event_production · pr_agency).
    if (cfg.subtypeEndpoint && subtypes.size === 0) return false;
    return true;
  })();
  const canSubmit    = canNextStep0 &&
    owner.owner_name.trim().length  >= 2 &&
    owner.owner_email.includes("@")     &&
    owner.owner_password.length     >= 6;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Fill in the required owner fields (name, email, password ≥ 6 chars).");
      return;
    }
    setBusy(true);
    try {
      const body = {
        org_type: kind,
        legal_name:         org.legal_name.trim(),
        brand_name:         org.brand_name.trim() || org.legal_name.trim(),
        registered_address: org.registered_address,
        gstin: org.gstin, pan: org.pan,
        ...owner,
      };
      if (cfg.hasStudioNetworkKind) body.studio_network_kind = org.studio_network_kind;
      if (cfg.hasTheaterHallFields) {
        body.registered_city  = org.theater_hall_registered_city.trim() || org.registered_address.split(",")[0].trim();
        body.theater_hall_stage_type   = org.theater_hall_stage_type;
        body.theater_hall_seats        = Number(org.theater_hall_seats) || 0;
        body.theater_hall_stage_width  = Number(org.theater_hall_stage_width) || 0;
        body.theater_hall_stage_depth  = Number(org.theater_hall_stage_depth) || 0;
        body.theater_hall_stage_height = Number(org.theater_hall_stage_height) || 0;
        body.theater_hall_tech_specs   = org.theater_hall_tech_specs || "";
        body.theater_hall_rate_card    = {
          evening:  Number(org.theater_hall_evening_rate) || 0,
          matinee:  Number(org.theater_hall_matinee_rate) || 0,
          currency: "INR",
        };
      }
      if (certifications.length)    body.industry_certifications = certifications;
      if (kind === "advertising_agency") {
        body.agency_types = agencyTypes;
        body.office_geofence_radius_m = Math.max(50, Math.min(1000, Number(officeGeoRadius) || 150));
        const cleanCoOwners = coOwners
          .map((c) => ({
            name:  (c.name  || "").trim(),
            email: (c.email || "").trim().toLowerCase(),
            phone: (c.phone || "").trim(),
          }))
          .filter((c) => c.name || c.email || c.phone);
        if (cleanCoOwners.length) body.co_owners = cleanCoOwners;
      }
      if (kind === "advertising_agency" && advAffiliations.length) {
        body.advertising_affiliations = advAffiliations;
      }
      if (usesGenericPicker && sectionAffiliations.length) {
        body.section_affiliations = sectionAffiliations;
      }
      if (cfg.subtypeEndpoint && subtypes.size) {
        body[cfg.subtypePayloadKey] = Array.from(subtypes);
      }
      const { data } = await axios.post(`${API}/onboard/org`, body);
      await setToken(data.access_token);
      await refresh();
      setApproved(data.organization);
      setStep(STEPS.length - 1);
      toast.success(`Welcome — ${data.organization.brand_name} is now verified`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create the organization");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10" data-testid={cfg.testidPrefix}>
      <OnboardBackButton step={step} onStepBack={back} label="Back to Options" />

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
             style={{ background: `${cfg.accent}22`, color: cfg.accent }}>
          <Icon size={22} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            {cfg.layer} · Sign Up
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">{cfg.label}</h1>
        </div>
      </div>
      <p className="mt-3 text-sm text-white/60 max-w-xl">{cfg.tagline}</p>

      <Stepper steps={STEPS} step={step} />

      {step === 0 && (
        <Card title="Company Details" testid={`${cfg.testidPrefix}-step-org`}>
          {kind === "advertising_agency" && agencyTypes.length > 0 && (
            <div className="rounded-xl border border-white/12 bg-white/[0.02] p-4"
                 data-testid="onboard-adv-agency-types-summary">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                    Agency Types Selected
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {agencyTypes.map((k) => {
                      const t = agencyTypeCatalog?.types?.find((x) => x.key === k);
                      return (
                        <span key={k}
                              className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.15em] uppercase font-semibold"
                              style={{ background: `${t?.accent || "#F59E0B"}22`, color: t?.accent || "#F59E0B" }}
                              data-testid={`onboard-adv-agency-type-chip-${k}`}>
                          {t?.label || k}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => nav("/onboard/agency-type", { replace: true })}
                  className="text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-white underline underline-offset-4"
                  data-testid="onboard-adv-agency-change-types"
                >
                  Change
                </button>
              </div>
            </div>
          )}
          <Field label={cfg.orgFieldLabel} tid={`${cfg.testidPrefix}-legal-name-field`}>
            <Input value={org.legal_name}
              onChange={(e) => updOrg("legal_name", e.target.value)}
              className="bg-black/40 border-white/15 h-11"
              placeholder="Full legal name as registered"
              data-testid={`${cfg.testidPrefix}-legal-name-input`} required />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={cfg.brandFieldLabel}>
              <Input value={org.brand_name}
                onChange={(e) => updOrg("brand_name", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                placeholder="Optional public brand"
                data-testid={`${cfg.testidPrefix}-brand-input`} />
            </Field>
            {cfg.hasStudioNetworkKind && (
              <Field label="Entity Kind">
                <Select value={org.studio_network_kind}
                  onValueChange={(v) => updOrg("studio_network_kind", v)}>
                  <SelectTrigger className="bg-black/40 border-white/15 h-11"
                                 data-testid={`${cfg.testidPrefix}-kind-select`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STUDIO_NETWORK_KINDS.map((k) => (
                      <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <Field label="Registered Address">
            <Textarea value={org.registered_address}
              onChange={(e) => updOrg("registered_address", e.target.value)}
              className="bg-black/40 border-white/15 min-h-[76px]"
              data-testid={`${cfg.testidPrefix}-address-input`} />
          </Field>
          {cfg.hasTheaterHallFields && (
            <>
              <Field label="City *" tid={`${cfg.testidPrefix}-city-field`}>
                <Input value={org.theater_hall_registered_city}
                  onChange={(e) => updOrg("theater_hall_registered_city", e.target.value)}
                  className="bg-black/40 border-white/15 h-11"
                  placeholder="e.g. Mumbai · Bangalore"
                  data-testid={`${cfg.testidPrefix}-city-input`} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Stage Type">
                  <Select value={org.theater_hall_stage_type}
                    onValueChange={(v) => updOrg("theater_hall_stage_type", v)}>
                    <SelectTrigger className="bg-black/40 border-white/15 h-11"
                                   data-testid={`${cfg.testidPrefix}-stage-type-select`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hallStageTypeCatalog.map((k) => (
                        <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Seat Capacity">
                  <Input type="number" min={0} max={5000}
                    value={org.theater_hall_seats}
                    onChange={(e) => updOrg("theater_hall_seats", e.target.value)}
                    className="bg-black/40 border-white/15 h-11"
                    data-testid={`${cfg.testidPrefix}-seats-input`} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Stage Width (m)">
                  <Input type="number" step="0.5" min={0}
                    value={org.theater_hall_stage_width}
                    onChange={(e) => updOrg("theater_hall_stage_width", e.target.value)}
                    className="bg-black/40 border-white/15 h-11"
                    data-testid={`${cfg.testidPrefix}-stage-width-input`} />
                </Field>
                <Field label="Stage Depth (m)">
                  <Input type="number" step="0.5" min={0}
                    value={org.theater_hall_stage_depth}
                    onChange={(e) => updOrg("theater_hall_stage_depth", e.target.value)}
                    className="bg-black/40 border-white/15 h-11"
                    data-testid={`${cfg.testidPrefix}-stage-depth-input`} />
                </Field>
                <Field label="Stage Height (m)">
                  <Input type="number" step="0.5" min={0}
                    value={org.theater_hall_stage_height}
                    onChange={(e) => updOrg("theater_hall_stage_height", e.target.value)}
                    className="bg-black/40 border-white/15 h-11"
                    data-testid={`${cfg.testidPrefix}-stage-height-input`} />
                </Field>
              </div>
              <Field label="Technical Specs (lighting rig, sound, wings…)">
                <Textarea value={org.theater_hall_tech_specs}
                  onChange={(e) => updOrg("theater_hall_tech_specs", e.target.value)}
                  placeholder="e.g. 24-channel lighting board · 2× wings SL/SR · full backstage crossover · dressing rooms x4"
                  className="bg-black/40 border-white/15 min-h-[80px]"
                  data-testid={`${cfg.testidPrefix}-tech-specs-input`} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Evening-slot rate (₹)">
                  <Input type="number" min={0} step="500"
                    value={org.theater_hall_evening_rate}
                    onChange={(e) => updOrg("theater_hall_evening_rate", e.target.value)}
                    className="bg-black/40 border-white/15 h-11"
                    data-testid={`${cfg.testidPrefix}-evening-rate-input`} />
                </Field>
                <Field label="Matinee-slot rate (₹)">
                  <Input type="number" min={0} step="500"
                    value={org.theater_hall_matinee_rate}
                    onChange={(e) => updOrg("theater_hall_matinee_rate", e.target.value)}
                    className="bg-black/40 border-white/15 h-11"
                    data-testid={`${cfg.testidPrefix}-matinee-rate-input`} />
                </Field>
              </div>
            </>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="GSTIN">
              <Input value={org.gstin}
                onChange={(e) => updOrg("gstin", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-gstin-input`} />
            </Field>
            <Field label="PAN">
              <Input value={org.pan}
                onChange={(e) => updOrg("pan", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-pan-input`} />
            </Field>
          </div>
          {kind === "advertising_agency" && (
            <Field label="Office check-in radius (metres) · default 150m">
              <Input type="number" min={50} max={1000} step={10}
                value={officeGeoRadius}
                onChange={(e) => setOfficeGeoRadius(e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid="onboard-adv-agency-geofence-input" />
              <div className="mt-1.5 text-[11px] text-white/45 leading-relaxed">
                Staff attendance pings must fall within this radius of the
                registered office. You can override this later from the
                Agency Office admin panel.
              </div>
            </Field>
          )}
          <NavRow onNext={next} disabledNext={!canNextStep0} />
          {cfg.subtypeEndpoint && (
            <div className="rounded-xl border border-white/12 bg-white/[0.02] p-4"
                 data-testid={`${cfg.testidPrefix}-subtype-picker`}>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                {cfg.subtypeLabel} *
              </div>
              <div className="mt-2 text-xs text-white/60 leading-relaxed">
                {cfg.subtypeHint}
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                {subtypeCatalog.map((t) => {
                  const on = subtypes.has(t.key);
                  return (
                    <button key={t.key} onClick={() => toggleSubtype(t.key)}
                      className={`p-3 rounded-lg text-left border transition-colors ${
                        on
                          ? "border-white/45"
                          : "bg-black/30 border-white/10 hover:bg-white/[0.05]"
                      }`}
                      style={on ? { borderColor: cfg.accent, background: `${cfg.accent}12` } : {}}
                      data-testid={`${cfg.testidPrefix}-subtype-${t.key}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-white">{t.label}</div>
                        {on && <Check size={14} style={{ color: cfg.accent }} />}
                      </div>
                      {t.blurb && (
                        <div className="mt-0.5 text-[11px] text-white/55">{t.blurb}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <CertificationsPicker
            orgType={kind}
            value={certifications}
            onChange={setCertifications}
            testidPrefix="onboard-cert"
          />
          {kind === "advertising_agency" && (
            <AdvertisingAffiliationsPicker
              value={advAffiliations}
              onChange={setAdvAffiliations}
              minRequired={1}
              testidPrefix="onboard-adv-aff"
            />
          )}
          {usesGenericPicker && (
            <AffiliationsPicker
              section={kind}
              value={sectionAffiliations}
              onChange={setSectionAffiliations}
              testidPrefix={`onboard-aff-${kind.replace(/_/g,"-")}`}
            />
          )}
        </Card>
      )}

      {step === 1 && (
        <Card title="Owner Account" testid={`${cfg.testidPrefix}-step-owner`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Owner Full Name">
              <Input value={owner.owner_name}
                onChange={(e) => updOwner("owner_name", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-owner-name-input`} required />
            </Field>
            <Field label="Owner Email">
              <Input type="email" value={owner.owner_email}
                onChange={(e) => updOwner("owner_email", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-owner-email-input`} required />
            </Field>
            <Field label="Phone">
              <Input value={owner.owner_phone}
                onChange={(e) => updOwner("owner_phone", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-owner-phone-input`} />
            </Field>
            <Field label="Password (min 6)">
              <Input type="password" value={owner.owner_password}
                onChange={(e) => updOwner("owner_password", e.target.value)}
                className="bg-black/40 border-white/15 h-11"
                data-testid={`${cfg.testidPrefix}-owner-password-input`} required minLength={6} />
            </Field>
          </div>

          {kind === "advertising_agency" && (
            <div className="mt-6 rounded-xl border border-white/12 bg-white/[0.02] p-4"
                 data-testid="onboard-adv-agency-co-owners">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                    Co-Owners · Up to 2
                  </div>
                  <div className="mt-1.5 text-[11px] text-white/50 leading-relaxed max-w-md">
                    Add up to 2 additional partners / co-heads. They receive an
                    invite (mocked in beta) and become admins of the agency once they claim it.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addCoOwner}
                  disabled={coOwners.length >= 2}
                  className="h-9 px-3 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:border-white hover:bg-white hover:text-black transition-all inline-flex items-center gap-1.5 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-white"
                  data-testid="onboard-adv-agency-add-co-owner"
                >
                  <Plus size={12} /> Add Co-Owner
                </button>
              </div>

              {coOwners.length > 0 && (
                <div className="mt-4 space-y-3">
                  {coOwners.map((c, idx) => (
                    <div key={idx}
                         className="rounded-lg border border-white/10 bg-black/25 p-3"
                         data-testid={`onboard-adv-agency-co-owner-row-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 font-semibold">
                          Co-Owner {idx + 1}
                        </div>
                        <button
                          type="button"
                          onClick={() => rmCoOwner(idx)}
                          className="text-white/40 hover:text-red-300 p-1"
                          data-testid={`onboard-adv-agency-co-owner-remove-${idx}`}
                          aria-label="Remove co-owner"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <Input value={c.name}
                          onChange={(e) => updCoOwner(idx, "name", e.target.value)}
                          placeholder="Full name"
                          className="bg-black/40 border-white/15 h-10 text-sm"
                          data-testid={`onboard-adv-agency-co-owner-name-${idx}`} />
                        <Input type="email" value={c.email}
                          onChange={(e) => updCoOwner(idx, "email", e.target.value)}
                          placeholder="Email"
                          className="bg-black/40 border-white/15 h-10 text-sm"
                          data-testid={`onboard-adv-agency-co-owner-email-${idx}`} />
                        <Input value={c.phone}
                          onChange={(e) => updCoOwner(idx, "phone", e.target.value)}
                          placeholder="Phone (optional)"
                          className="bg-black/40 border-white/15 h-10 text-sm"
                          data-testid={`onboard-adv-agency-co-owner-phone-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <NavRow onBack={back} onSubmit={submit}
                  submitting={busy}
                  disabledNext={!canSubmit}
                  submitLabel={busy ? "Creating…" : `Create ${cfg.label}`} />
        </Card>
      )}

      {step === 2 && approved && (
        <Card title="Approved · v1 Auto-Verify" testid={`${cfg.testidPrefix}-step-approved`}>
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display text-xl">Welcome, {approved.brand_name}!</h3>
              <p className="text-sm text-white/70 mt-1.5">
                Your {cfg.label} account is live. You can invite staff, add your first
                project, or configure permissions from the dashboard.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => nav("/dashboard")}
              className="btn-themed rounded-full h-11 px-6 text-xs uppercase tracking-[0.22em] font-semibold"
              data-testid={`${cfg.testidPrefix}-go-dashboard`}
            >
              Go to Dashboard →
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
